import {useCallback, useEffect, useRef, useState} from 'react';
import {
    needsYearEndConfirm,
    periodConfirmFingerprint,
} from '@/utils/auditPeriodSplit';
import type {FirstAuditConfirmResult} from '@/pages/components/company-info/company-annual/FirstAuditPeriodConfirmDialog';

type Patch = {
    firstAuditFollows18Months?: boolean | null;
    firstAuditYearEndMD?: string | null;
};

type Args = {
    periodFrom?: string | null;
    periodTo?: string | null;
    inCorpDate?: string | null;
    cessationDuringThisPeriod?: number | null;
    firstAuditYearEndMD?: string | null;
    /** Apply confirm / clear fields onto form */
    onPatch: (patch: Patch) => void;
};

/**
 * Step 2: opens year-end confirm when first audit + cessation + >1 year + YE missing.
 * Clears YE when period fingerprint changes.
 */
export function useFirstAuditPeriodConfirm({
    periodFrom,
    periodTo,
    inCorpDate,
    cessationDuringThisPeriod,
    firstAuditYearEndMD,
    onPatch,
}: Args) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const lastFpRef = useRef<string>('');
    const onPatchRef = useRef(onPatch);
    onPatchRef.current = onPatch;

    const fingerprint = periodConfirmFingerprint(periodFrom, periodTo);

    useEffect(() => {
        if (!periodFrom || !periodTo) {
            lastFpRef.current = fingerprint;
            return;
        }
        if (lastFpRef.current && lastFpRef.current !== fingerprint) {
            if (firstAuditYearEndMD != null) {
                onPatchRef.current({
                    firstAuditFollows18Months: null,
                    firstAuditYearEndMD: null,
                });
            }
            setDialogOpen(false);
        }
        lastFpRef.current = fingerprint;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to fingerprint
    }, [fingerprint]);

    useEffect(() => {
        const need = needsYearEndConfirm({
            periodFrom,
            periodTo,
            inCorpDate,
            cessationDuringThisPeriod,
            firstAuditYearEndMD,
        });
        setDialogOpen(need);
    }, [
        periodFrom,
        periodTo,
        inCorpDate,
        cessationDuringThisPeriod,
        firstAuditYearEndMD,
    ]);

    const handleConfirm = useCallback((result: FirstAuditConfirmResult) => {
        onPatchRef.current({
            firstAuditFollows18Months: null,
            firstAuditYearEndMD: result.yearEndMD,
        });
        setDialogOpen(false);
    }, []);

    const isBlocking = needsYearEndConfirm({
        periodFrom,
        periodTo,
        inCorpDate,
        cessationDuringThisPeriod,
        firstAuditYearEndMD,
    });

    return {
        dialogOpen,
        handleConfirm,
        isBlocking,
        blockingMessage:
            'Please confirm the financial year-end date (dd/MM) before saving.',
    };
}
